import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Target,
  Box,
  MessageCircleHeart,
  BarChart3,
  PenTool,
  Eye,
  Heart,
  Sparkles,
  ShieldCheck,
  MousePointerClick,
  Users,
} from 'lucide-react'

const STEPS = [
  {
    letter: 'D',
    name: 'Delimitar',
    tagline: 'Diagnóstico e leitura estratégica',
    icon: Target,
    desc: 'Nesta etapa, a Dorac identifica o contexto de negócio, a percepção atual da marca, as tensões do público, as objeções invisíveis e os gatilhos de relevância. O objetivo é entender como a audiência pensa, sente, compara e decide.',
    items: [
      'Diagnóstico completo do negócio e contexto de mercado',
      'Análise da percepção atual da marca pelo público',
      'Mapeamento de tensões, objeções e gatilhos de relevância',
      'Entendimento profundo da jornada de decisão do cliente',
      'Identificação dos pontos de atrito que impedem crescimento',
    ],
  },
  {
    letter: 'O',
    name: 'Organizar',
    tagline: 'Estruturação da jornada do cliente',
    icon: Box,
    desc: 'Aqui, a agência estrutura a jornada do cliente. Em vez de apenas distribuir posts em um funil, ela organiza uma progressão de percepção: ser notada, ser sentida, ser desejada, ser escolhida e ser recomendada.',
    items: [
      'Estruturação da jornada de conteúdo e comunicação',
      'Organização dos pilares do funil Dorac',
      'Definição de canais, formatos e frequência',
      'Alinhamento entre posicionamento e comunicação',
      'Roteiro estratégico de execução',
    ],
  },
  {
    letter: 'R',
    name: 'Ressignificar',
    tagline: 'Reengenharia Neurocomportamental do Conteúdo',
    icon: MessageCircleHeart,
    desc: 'Significa transformar estratégia em mensagens que ecoam emocional e simbolicamente no público. É o ponto em que a proposta de valor ganha linguagem, imagem, ritmo e repertório cultural.',
    items: [
      'Desenvolvimento da narrativa central da marca',
      'Criação de linguagem proprietária e tom de voz',
      'Construção de mensagens que ativam a persona',
      'Desenvolvimento de repertório visual e verbal',
      'Alinhamento entre emoção, razão e instinto',
    ],
  },
  {
    letter: 'A',
    name: 'Analisar',
    tagline: 'Auditoria e coerência de marca',
    icon: BarChart3,
    desc: 'A Dorac calibra tom de voz, narrativa, identidade verbal, provas de confiança, argumentos, ofertas e canais. Tudo precisa estar coerente com a percepção que a marca quer provocar.',
    items: [
      'Calibração do tom de voz e identidade verbal',
      'Análise de coerência entre posicionamento e execução',
      'Revisão de provas de confiança e autoridade',
      'Otimização de argumentos e ofertas',
      'Alinhamento de canais com a jornada do cliente',
    ],
  },
  {
    letter: 'C',
    name: 'Criar',
    tagline: 'Geração de movimento e conversão',
    icon: PenTool,
    desc: 'A etapa final: a comunicação precisa gerar movimento real por meio dos criativos para converter. Aqui não é apenas vender, é fazer o público avançar em direção à marca, ao relacionamento e à decisão.',
    items: [
      'Criação de conteúdo estratégico para todos os canais',
      'Desenvolvimento de campanhas de conversão',
      'Produção de criativos orientados a comportamento',
      'Implementação de gatilhos de decisão e conversão',
      'Monitoramento e otimização contínua dos resultados',
    ],
  },
]

const VECTORS = [
  {
    n: '01',
    name: 'Atenção',
    goal: 'Ser Notado (Quebra de padrão)',
    stage: 'Topo do Funil',
    mechanism: 'Filtro Reticular e Saliência',
    icon: Eye,
    desc: 'O cérebro ignora o comum. A atenção é capturada pelo contraste, pela novidade e pela relevância biológica imediata. Quebra de Padrão: criamos ganchos visuais e verbais que interrompem o scroll infinito, forçando o cérebro a processar a marca como algo prioritário.',
  },
  {
    n: '02',
    name: 'Emoção',
    goal: 'Ser Sentido (Conexão e identificação)',
    stage: 'Meio do Funil (Início)',
    mechanism: 'Marcadores Somáticos',
    icon: Heart,
    desc: "Emoções são atalhos para a memória. O sistema límbico rotula experiências como 'boas' ou 'ruins' antes mesmo da consciência. Storytelling Visceral: não apenas informamos, provocamos sensações. Usamos narrativas que geram identificação imediata, criando um rastro químico de conexão.",
  },
  {
    n: '03',
    name: 'Significado',
    goal: 'Ser Lembrado (Atribuição de valor e símbolo)',
    stage: 'Meio do Funil',
    mechanism: 'Redes Associativas e Identidade',
    icon: Sparkles,
    desc: "O ser humano é um animal simbólico. Atribuímos valor ao que reforça nossa identidade ou resolve nossas tensões. Posicionamento de Valor: transformamos o produto em um símbolo, criando camadas de significado que fazem o cliente sentir que a marca 'fala a língua dele' e reflete quem ele é.",
  },
  {
    n: '04',
    name: 'Confiança',
    goal: 'Ser Desejado/Seguro (Redução de incerteza)',
    stage: 'Fundo do Funil (Início)',
    mechanism: 'Vínculo e Redução de Incerteza',
    icon: ShieldCheck,
    desc: 'A confiança é a ausência de medo. O cérebro precisa de sinais de segurança para baixar a guarda e permitir a proximidade. Arquitetura de Autoridade: utilizamos provas sociais, consistência de tom e transparência radical para liberar ocitocina e eliminar as barreiras invisíveis da venda.',
  },
  {
    n: '05',
    name: 'Decisão',
    goal: 'Ser Escolhido (Ação e conversão)',
    stage: 'Fundo do Funil (Fim)',
    mechanism: 'Custo de Oportunidade e Impulso',
    icon: MousePointerClick,
    desc: "Toda decisão é uma escolha entre dor (perda de dinheiro) e prazer (ganho do benefício). A dopamina antecipa a recompensa. Engenharia de Conversão: facilitamos o 'sim', eliminamos a fricção cognitiva e apresentamos argumentos lógicos irrefutáveis e gatilhos de escassez que impulsionam a ação imediata.",
  },
  {
    n: '06',
    name: 'Pertencimento',
    goal: 'Ser Confirmada e Recomendada (Vinculo de retroalimentação)',
    stage: 'Pós-Funil (Flywheel)',
    mechanism: 'Identidade Social e Pertencimento',
    icon: Users,
    desc: 'Quando a entrega confirma a promessa, o cliente fortalece sua percepção de valor e sua identificação com a marca. Recomendar torna-se uma forma de compartilhar uma boa experiência e reafirmar sua escolha.	Comunidade e Continuidade: Criamos ações de pós-venda, reconhecimento, personalização e cuidado que transformam satisfação em vínculo, recorrência e recomendação espontânea.',
  },
]

function MethodPage({ idx, onNavigate }) {
  const isCover = idx === -1
  const isSummary = idx === 0
  const step = isCover || isSummary ? null : STEPS[idx - 1]

  if (isCover) {
    return (
      <div
        className='relative flex h-full min-h-[300px] w-full flex-1 flex-col items-center justify-center rounded-l-lg rounded-r-lg border border-dorac-border bg-white p-8 text-center md:rounded-l-none md:p-10'
        style={{
          boxShadow: '0 20px 45px rgba(73,28,71,0.12)',
        }}
      >
        <h2 className='mb-4 font-display text-5xl font-black text-dorac-purple md:text-6xl'>DORAC</h2>
        <p className='mb-10 max-w-sm text-sm font-semibold text-dorac-purple'>
          Humanos na essência. Estratégicos na forma. Inconfundíveis na intenção.
        </p>
        <button
          onClick={() => onNavigate(0)}
          className='method-cta-pulse rounded-full px-8 py-3 text-sm font-bold text-dorac-purple'
          style={{ backgroundColor: '#FFE98C' }}
        >
          Conhecer método
        </button>
        <style>{`
          @keyframes method-cta-pulse {
            0%, 20%, 100% { transform: translateY(0) rotate(0deg); }
            5% { transform: translateY(-8px) rotate(-3deg); }
            10% { transform: translateY(0) rotate(3deg); }
            15% { transform: translateY(-4px) rotate(-1deg); }
          }
          .method-cta-pulse {
            animation: method-cta-pulse 2.2s ease-in-out infinite;
          }
        `}</style>
      </div>
    )
  }

  if (isSummary) {
    return (
      <div
        className='relative flex h-full w-full flex-1 flex-col overflow-hidden rounded-l-lg rounded-r-lg border border-dorac-border bg-white p-6 md:rounded-l-none md:p-8'
        style={{ boxShadow: '0 20px 45px rgba(73,28,71,0.12)' }}
      >
        <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
          <span className='mb-1 block text-xs font-bold uppercase tracking-widest text-dorac-ink/40'>Sumário</span>
          <h2 className='mb-2 text-xl font-bold text-dorac-ink'>O Método DORAC</h2>
          <ul className='flex flex-col divide-y divide-dorac-border'>
            {STEPS.map((s, i) => (
              <li key={s.letter}>
                <button
                  onClick={() => onNavigate(i + 1)}
                  className='group flex w-full items-center gap-3 py-2 text-left'
                >
                  <span className='text-xs font-bold text-dorac-ink/30'>{String(i + 1).padStart(2, '0')}</span>
                  <span className='font-display text-lg font-black text-dorac-purple'>{s.letter}</span>
                  <div className='flex-1'>
                    <div className='text-sm font-bold text-dorac-ink transition-colors group-hover:text-dorac-purple'>
                      {s.name}
                    </div>
                    <div className='text-xs text-dorac-ink/50'>{s.tagline}</div>
                  </div>
                  <span className='text-dorac-ink/30 transition-colors group-hover:text-dorac-purple'>→</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-4 flex flex-shrink-0 items-center justify-between border-t border-dorac-border pt-4'>
          <button
            onClick={() => onNavigate(-1)}
            className='text-xs font-bold uppercase tracking-widest text-dorac-ink/40 transition-colors hover:text-dorac-purple'
          >
            ← Anterior
          </button>
          <span className='text-xs font-bold text-dorac-ink/30'>Sumário</span>
          <button
            onClick={() => onNavigate(1)}
            className='text-xs font-bold uppercase tracking-widest text-dorac-ink/40 transition-colors hover:text-dorac-purple'
          >
            Próxima →
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className='relative flex h-full w-full flex-1 flex-col rounded-l-lg rounded-r-lg border border-dorac-border bg-white p-6 md:rounded-l-none md:p-8'
      style={{ boxShadow: '0 20px 45px rgba(73,28,71,0.12)' }}
    >
      <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
        <span className='font-display text-6xl font-black text-dorac-purple'>{step.letter}</span>
        <h2 className='mb-0.5 mt-2 text-lg font-bold text-dorac-ink'>{step.name}</h2>
        <p className='mb-2 text-xs font-semibold text-dorac-purple'>{step.tagline}</p>
        <p className='mb-3 text-xs leading-relaxed text-dorac-ink/70'>{step.desc}</p>
        <span className='mb-2 block text-[10px] font-bold uppercase tracking-widest text-dorac-ink/40'>
          O que fazemos nesta etapa
        </span>
        <ul className='flex flex-col gap-1'>
          {step.items.map((item) => (
            <li key={item} className='flex items-start gap-2 text-xs text-dorac-ink/70'>
              <span className='mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-dorac-purple' />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className='mt-3 flex flex-shrink-0 items-center justify-between border-t border-dorac-border pt-3'>
        <button
          onClick={() => onNavigate(idx - 1)}
          className='text-xs font-bold uppercase tracking-widest text-dorac-ink/40 transition-colors hover:text-dorac-purple'
        >
          ← Anterior
        </button>
        <span className='text-xs font-bold text-dorac-ink/30'>
          {String(idx).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')}
        </span>
        <button
          onClick={() => onNavigate(idx === STEPS.length ? -1 : idx + 1)}
          className='text-xs font-bold uppercase tracking-widest text-dorac-ink/40 transition-colors hover:text-dorac-purple'
        >
          {idx === STEPS.length ? 'Voltar à capa →' : 'Próxima →'}
        </button>
      </div>
    </div>
  )
}

function MethodContent() {
  const [active, setActive] = useState(-1)
  const [overlay, setOverlay] = useState(null)

  const goTo = (idx) => {
    if (idx === active) return
    setOverlay({ idx: active, key: Date.now() })
    setActive(idx)
  }

  return (
    <>
      <section className='dorac-grid-bg py-20 pt-32'>
        <div className='container mx-auto px-4'>
          <div className='mx-auto flex max-w-5xl flex-col items-center gap-10 lg:flex-row lg:items-stretch lg:gap-16'>
            <div className='flex flex-col justify-center text-center lg:max-w-sm lg:text-left'>
              <span className='mb-3 block text-xs font-bold uppercase tracking-widest text-dorac-purple'>
                Nossa metodologia proprietária
              </span>
              <h1 className='section-title mb-6'>
                O Método <span className='text-dorac-purple'>DORAC</span>
              </h1>
              <p className='text-base leading-relaxed text-dorac-ink/60 md:text-lg'>
                As 5 etapas que transformam leitura humana em estratégia aplicada, posicionamento forte e conversão
                consistente. Não começamos no post, começamos pelas pessoas.
              </p>
            </div>

            <div className='flex w-full flex-1 items-stretch'>
              <div
                className='z-20 mr-[-4px] hidden w-8 flex-shrink-0 flex-col items-center justify-evenly rounded-l-2xl md:flex'
                style={{ background: '#491C47' }}
              >
                {Array.from({ length: 7 }).map((_, i) => (
                  <span key={i} className='h-3 w-3 rounded-full bg-white/90 shadow-inner' />
                ))}
              </div>

              <div
                className='relative h-[600px] flex-1 overflow-hidden rounded-l-lg rounded-r-lg md:h-[540px] md:rounded-l-none'
                style={{ perspective: 1600 }}
              >
              <div
                className='absolute inset-0 rounded-l-lg rounded-r-lg border border-dorac-border bg-white md:rounded-l-none'
                style={{ transform: 'translate(10px, 10px)', boxShadow: '0 10px 25px rgba(73,28,71,0.08)' }}
              />
              <div
                className='absolute inset-0 rounded-l-lg rounded-r-lg border border-dorac-border bg-white md:rounded-l-none'
                style={{ transform: 'translate(5px, 5px)', boxShadow: '0 10px 25px rgba(73,28,71,0.1)' }}
              />

              <div key={active} className='method-page-in absolute inset-0'>
                <MethodPage idx={active} onNavigate={goTo} />
              </div>

              {overlay && (
                <div
                  key={overlay.key}
                  className='method-page-tear absolute inset-0'
                  onAnimationEnd={() => setOverlay(null)}
                >
                  <MethodPage idx={overlay.idx} onNavigate={goTo} />
                  <div className='method-page-tear-shade pointer-events-none absolute inset-0' />
                </div>
              )}
              </div>
            </div>
          </div>

          <style>{`
            @keyframes method-page-out {
              0% { transform: scale(1) translateY(0); opacity: 1; filter: blur(0px); }
              100% { transform: scale(0.96) translateY(-10px); opacity: 0; filter: blur(6px); }
            }
            @keyframes method-page-in {
              0% { transform: scale(1.03); opacity: 0; filter: blur(4px); }
              100% { transform: scale(1); opacity: 1; filter: blur(0px); }
            }
            .method-page-in {
              animation: method-page-in 0.5s cubic-bezier(0.32, 0.72, 0, 1) forwards;
              will-change: transform, opacity, filter;
            }
            @keyframes method-page-tear {
              0% {
                transform: translate(0, 0) rotate(0deg);
                opacity: 1;
                clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
              }
              30% {
                transform: translate(2px, -2px) rotate(-0.5deg);
                clip-path: polygon(
                  3% 0%, 100% 0%, 100% 100%, 0% 100%,
                  0% 62%, 4% 58%, 1% 53%, 5% 49%, 2% 44%,
                  6% 40%, 2% 35%, 5% 30%, 1% 25%, 4% 20%, 0% 15%
                );
              }
              55% {
                transform: translate(6px, -4px) rotate(-1deg);
                opacity: 1;
                clip-path: polygon(
                  3% 0%, 100% 0%, 100% 100%, 0% 100%,
                  0% 62%, 4% 58%, 1% 53%, 5% 49%, 2% 44%,
                  6% 40%, 2% 35%, 5% 30%, 1% 25%, 4% 20%, 0% 15%
                );
              }
              100% {
                transform: translate(120%, 45%) rotate(22deg);
                opacity: 0;
                clip-path: polygon(
                  3% 0%, 100% 0%, 100% 100%, 0% 100%,
                  0% 62%, 4% 58%, 1% 53%, 5% 49%, 2% 44%,
                  6% 40%, 2% 35%, 5% 30%, 1% 25%, 4% 20%, 0% 15%
                );
              }
            }
            @keyframes method-page-tear-shade {
              0% { opacity: 0; }
              30% { opacity: 0.25; }
              55% { opacity: 0.3; }
              100% { opacity: 0.45; }
            }
            .method-page-tear {
              animation: method-page-tear 0.5s cubic-bezier(0.32, 0.72, 0, 1) forwards;
              box-shadow: 12px 0 30px rgba(0,0,0,0.16);
              will-change: transform, clip-path, opacity;
            }
            .method-page-tear-shade {
              background: linear-gradient(120deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 55%);
              animation: method-page-tear-shade 0.5s ease forwards;
            }
          `}</style>
        </div>
      </section>

      <section className='dorac-grid-bg-dark py-24 text-white'>
        <div className='container mx-auto px-4'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <span className='mb-3 block text-xs font-bold uppercase tracking-widest' style={{ color: '#FFE98C' }}>
              Ciência do comportamento aplicada
            </span>
            <h2 className='mb-4 text-3xl font-bold md:text-4xl'>O Funil Neurocientífico Dorac</h2>
            <p className='leading-relaxed text-white/60'>
              6 pilares baseados em neurociência que guiam o público desde a atenção até o pertencimento. Cada etapa
              ativa mecanismos cerebrais específicos para construir relacionamento e gerar decisão.
            </p>
          </div>

          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
            {VECTORS.map((v) => {
              return (
                <div
                  key={v.n}
                  className='rounded-2xl border border-white/10 p-6'
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <div className='mb-4 flex items-center justify-between'>
                    <span className='text-xs font-bold tracking-widest text-white/30'>{v.n}</span>
                  </div>
                  <h3 className='mb-1 text-xl font-bold'>{v.name}</h3>
                  <p className='mb-4 text-sm font-semibold' style={{ color: '#FFE98C' }}>
                    {v.goal}
                  </p>
                  <p className='mb-2 text-xs font-bold uppercase tracking-wide text-white/40'>
                    Mecanismo: {v.mechanism}
                  </p>
                  <p className='text-sm leading-relaxed text-white/60'>{v.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className='dorac-grid-bg py-24'>
        <div className='container mx-auto max-w-2xl px-4 text-center'>
          <h2 className='section-title mb-6'>
            Quer aplicar o Método DORAC
            <br />
            na sua marca?
          </h2>
          <p className='section-subtitle mx-auto mb-8'>
            Agende um diagnóstico estratégico e descubra como transformar comportamento humano em posicionamento,
            narrativa e conversão para o seu negócio.
          </p>
          <Link to='/contato' className='btn-dorac gap-2 px-8 py-4'>
            Solicitar diagnóstico gratuito
            <ArrowRight className='h-4 w-4' />
          </Link>
        </div>
      </section>
    </>
  )
}

export default MethodContent
